/// <amd-module name="Wit/Common/Controls/Boards/BoardFilterDataSource" />
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IClientWorkItem, IObservableClientWorkItem, ObservedWorkItem, ParentField } from "Wit/ClientWorkItemService/IClientWorkItem";
import { IClientWorkItemService } from "Wit/ClientWorkItemService/IClientWorkItemService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { getAreaPathLeafNode } from "Wit/Filter/AreaPathHelper";
import { FilterValue, IBoardFilterDataSource, IParentFilterItem } from "Wit/Filter/FilterManager";
import { getUniqueValuesForAssignedTo } from "Wit/Filter/Utilities/Utilities";

export class BoardFilterDataSource<T extends IObservableClientWorkItem<U>, U extends IClientWorkItem = ObservedWorkItem<T>>
    implements IBoardFilterDataSource
{
    protected workItemService: IClientWorkItemService<T, U>;
    private visibleFields: string[];

    constructor(workItemService: IClientWorkItemService<T, U>, visibleFields: string[], private context: IVssPageContext) {
        this.workItemService = workItemService;
        this.visibleFields = visibleFields;
    }

    public getDataSourceName = () => "BoardFilterDataSource";

    public getItemCount(): number {
        return this.workItemService.getAllItems().length;
    }

    public getIds(): number[] {
        return this.workItemService.getAllItems().map(item => item.value.id);
    }

    public getValue(id: number, fieldName: string) {
        return this.workItemService.get(id)?.value.getFieldValue(fieldName);
    }

    public getVisibleFields(): string[] {
        return this.visibleFields;
    }

    public getUniqueValues(fieldName: string): FilterValue[] | Promise<FilterValue[]> {
        const uniqueValues = new Set<string>();

        // special case when field value isn't a string
        const uniqueObjects: IdentityRef[] = [];
        for (const item of this.workItemService.getAllItems()) {
            const asIdentityRef = fieldName === CoreFieldRefNames.AssignedTo;
            const fieldValue = item.value.getFieldValue<any>(fieldName, undefined, undefined, asIdentityRef);

            if (!fieldValue) {
                continue;
            }

            if (fieldName === CoreFieldRefNames.Tags) {
                const tags = (fieldValue as string).split(";");
                tags.forEach(t => uniqueValues.add(t.trim()));
            } else if (fieldName === CoreFieldRefNames.AssignedTo) {
                getUniqueValuesForAssignedTo(this.context, fieldValue, uniqueValues, uniqueObjects);
            } else if (fieldName === CoreFieldRefNames.AreaPath) {
                uniqueValues.add(getAreaPathLeafNode(fieldValue));
            } else {
                uniqueValues.add(fieldValue.toString());
            }
        }

        return uniqueObjects.length > 0
            ? uniqueObjects.sort((a, b) => localeIgnoreCaseComparer(a.displayName, b.displayName))
            : Array.from(uniqueValues).sort(localeIgnoreCaseComparer);
    }

    public getParentItems(): IParentFilterItem[] {
        const parentFilterItems = new Map<number, IParentFilterItem>();
        for (const item of this.workItemService.getAllItems()) {
            const parentId = item.value.getFieldValue<number>(CoreFieldRefNames.Parent);
            const parentName = item.value.getFieldValue<string>(ParentField.Title);

            if (!parentId || !parentName || parentFilterItems.has(parentId)) {
                continue;
            }

            parentFilterItems.set(parentId, { id: parentId, title: parentName });
        }

        return [...parentFilterItems.values()].sort((a, b) => localeIgnoreCaseComparer(a.title, b.title));
    }

    public getAllParents(id: number): number[] {
        const parentIds = [];
        let current = this.workItemService.get(id)?.value.getFieldValue<number>(CoreFieldRefNames.Parent);
        while (current) {
            parentIds.push(current);
            current = this.workItemService.get(current)?.value.getFieldValue<number>(CoreFieldRefNames.Parent);
        }

        return parentIds;
    }
}
